import { useMemo, useState } from "react";
import { useThemeSlice } from "../../../../components/ui/StatusBadge.jsx";
import { DeviceDebugCommands } from "./DeviceDebugCommands.jsx";
import { DEBUG_STREAM_TABS, formatJson, getDebugStreamPayload } from "./deviceDebugMock.js";
import { buildDebugPanelTokens } from "./deviceDebugTheme.js";

export function DeviceDebugStreamPanel({ device, onAction }) {
  const { c, dark, accentHex: A } = useThemeSlice();
  const tk = useMemo(() => buildDebugPanelTokens(c, dark, A), [c, dark, A]);
  const [stream, setStream] = useState("gpscan");
  const [mapMode, setMapMode] = useState("satellite");
  const [showTimestamp, setShowTimestamp] = useState(true);

  const payload = useMemo(() => getDebugStreamPayload(stream), [stream, device?.id]);
  const primaryJson = useMemo(() => formatJson(payload.primary), [payload.primary]);
  const secondaryJson = useMemo(() => formatJson(payload.secondary), [payload.secondary]);

  return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0, minHeight: 0, background: c.sb, padding: "12px 14px", gap: 10, overflow: "hidden" }}>
      <div style={{ position: "relative", height: 200, flexShrink: 0, borderRadius: tk.radius, overflow: "hidden", border: `1px solid ${c.cardBdr}`, boxShadow: tk.shadowSm }}>
        <MapPlaceholder c={c} dark={dark} mode={mapMode} />
        <div style={{ position: "absolute", top: 10, left: 10, display: "flex", gap: 2, background: c.card, border: `1px solid ${c.cardBdr}`, borderRadius: tk.radiusSm, padding: 3, boxShadow: tk.shadowSm }}>
          {["map", "satellite"].map((mode) => {
            const active = mapMode === mode;
            return (
              <button
                key={mode}
                type="button"
                onClick={() => setMapMode(mode)}
                style={{
                  border: "none",
                  borderRadius: 6,
                  padding: "4px 10px",
                  fontSize: 10.5,
                  fontWeight: 700,
                  cursor: "pointer",
                  background: active ? `${A}18` : "transparent",
                  color: active ? A : c.mid,
                  textTransform: "capitalize",
                }}
              >
                {mode}
              </button>
            );
          })}
        </div>
        <button type="button" aria-label="Full screen map" style={{ position: "absolute", top: 10, right: 10, ...iconBtn(c) }}>
          <FullscreenIcon />
        </button>
      </div>

      <DeviceDebugCommands onAction={onAction} />

      <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 10px", borderRadius: tk.radius, border: `1px solid ${c.cardBdr}`, background: c.card, boxShadow: tk.shadowSm, flexShrink: 0, overflowX: "auto" }}>
        <div style={{ display: "flex", gap: 4, flex: 1, minWidth: 0, overflowX: "auto" }}>
          {DEBUG_STREAM_TABS.map((tab) => {
            const active = stream === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => setStream(tab.id)}
                style={{
                  border: `1px solid ${active ? `${A}45` : "transparent"}`,
                  borderRadius: 999,
                  padding: "5px 11px",
                  fontSize: 10.5,
                  fontWeight: 700,
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                  background: active ? `${A}12` : c.hover,
                  color: active ? A : c.mid,
                  fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                }}
              >
                {tab.label}
              </button>
            );
          })}
        </div>
        <button type="button" aria-label="Download stream" style={iconBtn(c)}>
          <DownloadIcon />
        </button>
      </div>

      <div style={{ flex: 1, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, minHeight: 0, overflow: "hidden" }}>
        <JsonPanel title="Live stream" json={primaryJson} c={c} dark={dark} A={A} tk={tk} />
        <JsonPanel
          title="CAN / GNSS"
          json={secondaryJson}
          c={c}
          dark={dark}
          A={A}
          tk={tk}
          showTimestamp={showTimestamp}
          onToggleTimestamp={() => setShowTimestamp((v) => !v)}
        />
      </div>
    </div>
  );
}

function MapPlaceholder({ c, dark, mode }) {
  return (
    <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", position: "relative", overflow: "hidden" }}>
      <div
        style={{
          position: "absolute",
          inset: 0,
          background: mode === "satellite"
            ? dark
              ? "radial-gradient(circle at 35% 42%, #3a4a38 0%, #1a1d24 58%, #101218 100%)"
              : "radial-gradient(circle at 35% 42%, #9CB396 0%, #6E8568 42%, #556B4F 100%)"
            : dark
              ? "linear-gradient(180deg, #2a3140 0%, #151820 100%)"
              : "linear-gradient(180deg, #EDE9E3 0%, #D5D0C8 100%)",
        }}
      />
      <div style={{ position: "absolute", inset: 0, opacity: 0.08, backgroundImage: "linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)", backgroundSize: "24px 24px" }} />
      <div style={{ position: "relative", zIndex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
        <span style={{ width: 12, height: 12, borderRadius: "50% 50% 50% 0", background: A_PIN, transform: "rotate(-45deg)", boxShadow: "0 2px 10px rgba(239,68,68,.45)" }} />
        <span style={{ fontSize: 10.5, fontWeight: 600, color: c.text, background: c.card, border: `1px solid ${c.cardBdr}`, padding: "4px 10px", borderRadius: 999, boxShadow: "0 2px 8px rgba(0,0,0,.08)" }}>12.9716, 77.5946</span>
      </div>
    </div>
  );
}

const A_PIN = "#EF4444";

function JsonPanel({ title, json, c, dark, A, tk, showTimestamp, onToggleTimestamp }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", minHeight: 0, borderRadius: tk.radius, border: `1px solid ${tk.codeBorder}`, overflow: "hidden", background: c.card, boxShadow: tk.shadowSm }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, padding: "9px 12px", borderBottom: `1px solid ${c.divider}`, background: c.hover, flexShrink: 0 }}>
        <span style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase", color: c.sub }}>{title}</span>
        {onToggleTimestamp && (
          <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 10.5, color: c.sub, cursor: "pointer", userSelect: "none" }}>
            <input type="checkbox" checked={showTimestamp} onChange={onToggleTimestamp} style={{ accentColor: A, width: 13, height: 13 }} />
            Timestamps
          </label>
        )}
      </div>
      <pre
        style={{
          flex: 1,
          margin: 0,
          padding: "12px 14px",
          overflow: "auto",
          fontSize: 11,
          lineHeight: 1.55,
          fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
          color: dark ? "#CBD5E1" : "#334155",
          background: tk.codeBg,
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
        }}
      >
        {json}
      </pre>
    </div>
  );
}

function iconBtn(c) {
  return {
    width: 28,
    height: 28,
    borderRadius: 7,
    border: `1px solid ${c.cardBdr}`,
    background: c.card,
    color: c.mid,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    boxShadow: "0 1px 2px rgba(0,0,0,.06)",
  };
}

function DownloadIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7,10 12,15 17,10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </svg>
  );
}

function FullscreenIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M8 3H5a2 2 0 0 0-2 2v3M21 8V5a2 2 0 0 0-2-2h-3M3 16v3a2 2 0 0 0 2 2h3M16 21h3a2 2 0 0 0 2-2v-3" />
    </svg>
  );
}
